import * as React from "react";
import {EquipmentFilterV7} from "../../data/models/EquipmentFilters";
import {addEntriesToCensus, createEquipmentFilter,} from "../../data/controllers/CensusCoolerController";
import {uuid, VeboxModels} from "../../../../../types/veboxModels";
import {DataResponse} from "../../data/models/RestApi";
import {CensusCoolerContext} from "../CensusCollerProvider";
import {JSONCensus} from "../../data/models/JSONCensus";
import {
    useDeviceModels,
    useEquipmentGuidsWithFilterList,
    useEquipmentWithFilterList,
    useVisibleHierarchy,
} from "../../data/hooks/CecnsusCoolerControllerHooks";
import {SWRResponse} from "swr";
import {UseFetchEquipmentWithFilterListInfiniteHookProps} from "../../data/hooks/UseEquipmentWithFilterListHook";
import {ADDITIONAL_FILTERS, baseFilter, DEFAULT_EXCLUSION_FILTERS,} from "../../types";
import {addVeBoxFilter, isFilterOldEmpty, removeVeBoxFilter, replaceVeBoxFilter,} from "../../utils";
import {DATA_STATE} from "../../../../../users/contexts/UsersDashboardContext";
import {useDebounce} from "@vebox/etui-core";
import {DefineEquipmentPageProviderContext} from "../DefineEquipmentPageProvider";
import {addOrRemoveDefaultFilterUtils, createDefaultFiltersUtils,} from "./utils";
import {JSONList} from "../../data/models/JSONList";
import {VeBoxEquipmentFilters} from "../../data/models/VeBoxEquipmentFilters";

const MAX_EQUIPMENT_PER_REQUEST = 5000;

interface FindEquipmentContextType {
    addOrRemoveDefaultFilter: (
        filter: DEFAULT_EXCLUSION_FILTERS,
    ) => (event) => void;
    defaultFilters: VeBoxEquipmentFilters;
    selectedDefaultFilter?: DEFAULT_EXCLUSION_FILTERS[];
    selectedAdditionalFilter?: ADDITIONAL_FILTERS[];
    additionalVeBoxFilters: VeBoxEquipmentFilters;
    applyFilterAndSearch: () => void;
    filterResponse: EquipmentFilterV7;
    equipmentList: UseFetchEquipmentWithFilterListInfiniteHookProps;
    equipmentGuidsList: SWRResponse<uuid[], Error, any>;
    removeAllFilters: () => void;
    selectedEquipment: uuid[];
    updateSelectedEquipment: (equipment: uuid[]) => void;
    addComponentsToCensus: () => void;
    censusWithNewEntities: DataResponse<JSONCensus>;
    createDefaultFilters: () => void;
    areAllSelected: boolean;
    updateAreAllSelected: (value: boolean) => void;
    handleVeBoxAdditionalFilters: (
        action: "remove" | "add" | "replace",
        value: VeBoxEquipmentFilters,
        filter: ADDITIONAL_FILTERS,
    ) => void;
    setIsRefreshingTable: (value: boolean) => void;
    isRefreshingTable: boolean;
    refreshList: () => Promise<void>;
    setShouldFetchSiteFilters: (value: boolean) => void;
    setShouldFetchTelemetryFilters: (value: boolean) => void;
    deviceModels: SWRResponse<JSONList<VeboxModels.DeviceModel>, Error, any>;
    hierarchyVisibleByTheUser: SWRResponse<
        JSONList<VeboxModels.HierarchiesTree>,
        Error,
        any
    >;
    isRemovingAll: boolean;
    removeCensusErrors: () => void;
}

interface FindEquipmentProviderProps {
}

export const FindEquipmentContext =
    React.createContext<FindEquipmentContextType>({} as any);

export const FindEquipmentProvider: React.FC<FindEquipmentProviderProps> = ({
                                                                                children,
                                                                            }) => {
    const {acceptedEquipmentStatuses, createCensus} =
        React.useContext(CensusCoolerContext);
    const {fetchFirstEntriesList, refreshEquipmentList} = React.useContext(
        DefineEquipmentPageProviderContext,
    );

    const [additionalVeBoxFilters, setAdditionalVeBoxFilters] =
        useDebounce<VeBoxEquipmentFilters>({}, 500);
    const [defaultFilters, setDefaultFilters] =
        React.useState<VeBoxEquipmentFilters>();
    const [filterResponse, setFilterResponse] =
        React.useState<EquipmentFilterV7>();
    const [selectedEquipment, setSelectedEquipment] = React.useState<uuid[]>([]);
    const [selectedDefaultFilter, setSelectedDefaultFilter] = React.useState<
        DEFAULT_EXCLUSION_FILTERS[]
    >([]);
    const [selectedAdditionalFilter, setSelectedAdditionalFilter] =
        React.useState<ADDITIONAL_FILTERS[]>([]);
    const [areAllSelected, setAreAllSelected] = React.useState<boolean>(true);
    const [censusWithNewEntities, setCensusWithNewEntities] = React.useState<
        DataResponse<JSONCensus>
    >({data: undefined, error: undefined, isLoading: false});
    const [isRefreshingTable, setIsRefreshingTable] =
        React.useState<boolean>(false);
    const [isRemovingAll, setIsRemovingAll] = React.useState<boolean>(false);

    const [shouldFetchEquipment, setShouldFetchEquipment] =
        React.useState<boolean>(false);
    const equipmentListRequestParams: any = React.useMemo(
        () => ({
            offset: 0,
            limit: 40,
            filterGuid: filterResponse?.guid,
            shouldFetch: shouldFetchEquipment,
        }),
        [filterResponse, shouldFetchEquipment],
    );
    const equipmentList = useEquipmentWithFilterList(equipmentListRequestParams);
    const equipmentGuidsList = useEquipmentGuidsWithFilterList(
        equipmentListRequestParams?.filterGuid,
        equipmentListRequestParams?.shouldFetch,
    );

    const [shouldFetchSiteFilters, setShouldFetchSiteFilters] =
        React.useState<boolean>(false);
    const hierarchyVisibleByTheUser = useVisibleHierarchy(shouldFetchSiteFilters);
    const [shouldFetchTelemetryFilters, setShouldFetchTelemetryFilters] =
        React.useState<boolean>(false);
    const deviceModels = useDeviceModels(shouldFetchTelemetryFilters);

    const removeAllFilters = () => {
        setIsRemovingAll(true);
        setDefaultFilters({});
        setAdditionalVeBoxFilters({});
        setSelectedDefaultFilter([]);
        setSelectedAdditionalFilter([]);
    };

    const updateSelectedEquipment = (equipment: uuid[]) => {
        setSelectedEquipment(equipment);
    };

    const createDefaultFilters = () => {
        const result = createDefaultFiltersUtils(acceptedEquipmentStatuses.data);
        setSelectedDefaultFilter(result.selectedDefaultFilter);
        setDefaultFilters(result.newFilters);
    };

    const addOrRemoveDefaultFilter =
        (filter: DEFAULT_EXCLUSION_FILTERS) => (event) => {
            setIsRemovingAll(false);
            const result = addOrRemoveDefaultFilterUtils(
                filter,
                selectedDefaultFilter,
                defaultFilters,
                acceptedEquipmentStatuses.data,
                event,
            );
            setSelectedDefaultFilter(result.newSelectedDefaultFilter);
            setDefaultFilters(result.defaultFilters);
        };

    const handleVeBoxAdditionalFilters = (
        action: "remove" | "add" | "replace",
        newFilters: VeBoxEquipmentFilters,
        filter: ADDITIONAL_FILTERS,
    ) => {
        setIsRemovingAll(false);
        const isFilterIncluded = selectedAdditionalFilter.includes(filter);
        const actionToPerform =
            action === "add" && isFilterIncluded ? "replace" : action;

        if (actionToPerform === "remove" && isFilterIncluded) {
            setAdditionalVeBoxFilters(
                removeVeBoxFilter(additionalVeBoxFilters, newFilters),
            );
            setSelectedAdditionalFilter(
                selectedAdditionalFilter.filter((f) => f !== filter),
            );
        } else if (actionToPerform === "add") {
            setSelectedAdditionalFilter([...selectedAdditionalFilter, filter]);
            setAdditionalVeBoxFilters(
                addVeBoxFilter(additionalVeBoxFilters, newFilters),
            );
        } else if (actionToPerform === "replace") {
            if (!selectedAdditionalFilter.includes(filter)) {
                setSelectedAdditionalFilter([...selectedAdditionalFilter, filter]);
            }
            setAdditionalVeBoxFilters(
                replaceVeBoxFilter(additionalVeBoxFilters, newFilters),
            );
        }
    };

    const applyFilterAndSearch = (): void => {
        if (
            isFilterOldEmpty(defaultFilters) &&
            isFilterOldEmpty(additionalVeBoxFilters)
        ) {
            findEquipment();
        } else {
            createEquipmentFilter(
                addVeBoxFilter(
                    baseFilter,
                    addVeBoxFilter(defaultFilters, additionalVeBoxFilters),
                ),
            )
                .then((response) => {
                    setFilterResponse(response);
                })
                .catch(console.error);
        }
    };

    const refreshList = async () => {
        setIsRefreshingTable(true);
        await refreshEquipmentList();
        setIsRefreshingTable(false);
    };

    const addComponentsToCensusWithMultipleRequests = async (censusGuid: string): Promise<JSONCensus> => {
        const equipmentChunks = selectedEquipment.reduce(
            (acc, curr, index) => {
                const chunkIndex = Math.floor(index / MAX_EQUIPMENT_PER_REQUEST);
                if (!acc[chunkIndex]) {
                    acc[chunkIndex] = [];
                }
                acc[chunkIndex].push(curr);
                return acc;
            },
            [] as uuid[][],
        );
        const censusPromises = equipmentChunks.map((chunk) =>
            addEntriesToCensus(censusGuid, chunk),
        );
        const responses = await Promise.all(censusPromises);
        return responses[censusPromises.length - 1];
    }

    const addComponentsToCensus = async () => {
        setCensusWithNewEntities({...censusWithNewEntities, isLoading: true});
        try {
            const census = await createCensus();
            let response: JSONCensus;
            if (selectedEquipment.length > MAX_EQUIPMENT_PER_REQUEST) {
                response = await addComponentsToCensusWithMultipleRequests(census.guid);
            } else {
                response = await addEntriesToCensus(census.guid, selectedEquipment);
            }
            setCensusWithNewEntities({
                ...censusWithNewEntities,
                isLoading: false,
                data: response,
            });
            if (!fetchFirstEntriesList()) {
                await refreshList();
            }
        } catch (error) {
            console.error(error)
            setCensusWithNewEntities({
                ...censusWithNewEntities,
                isLoading: false,
                error,
            });
        }
    };

    const removeCensusErrors = () => {
        setCensusWithNewEntities({...censusWithNewEntities, error: undefined});
    }

    const findEquipment = () => {
        setShouldFetchEquipment(true);
    };

    const updateAreAllSelected = (value: boolean) => {
        setAreAllSelected(value);
    };

    React.useEffect(() => {
        if (filterResponse) {
            findEquipment();
        }
    }, [filterResponse]);

    React.useEffect(() => {
        if (acceptedEquipmentStatuses.status === DATA_STATE.loaded) {
            createDefaultFilters();
        }
    }, [acceptedEquipmentStatuses.status]);

    return (
        <FindEquipmentContext.Provider
            value={{
                addOrRemoveDefaultFilter,
                defaultFilters,
                selectedDefaultFilter,
                selectedAdditionalFilter,
                additionalVeBoxFilters,
                applyFilterAndSearch,
                filterResponse,
                equipmentList,
                equipmentGuidsList,
                removeAllFilters,
                selectedEquipment,
                updateSelectedEquipment,
                addComponentsToCensus,
                censusWithNewEntities,
                createDefaultFilters,
                areAllSelected,
                updateAreAllSelected,
                handleVeBoxAdditionalFilters,
                setIsRefreshingTable,
                isRefreshingTable,
                refreshList,
                setShouldFetchSiteFilters,
                setShouldFetchTelemetryFilters,
                deviceModels,
                hierarchyVisibleByTheUser,
                isRemovingAll,
                removeCensusErrors
            }}
        >
            {children}
        </FindEquipmentContext.Provider>
    );
};
